import React, { useEffect, useState } from 'react';
import { Instagram, Facebook, Phone, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import Logo from './Logo';
import FadeInWhenVisible from './FadeInWhenVisible';
import { supabase } from '../lib/supabase';

interface SocialLink {
  platform: string;
  url: string;
  visible: boolean;
}

const Footer = () => {
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [socialText, setSocialText] = useState('');

  useEffect(() => {
    fetchSocialLinks();
    fetchSocialText();
  }, []);

  const fetchSocialText = async () => {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'footer_social_text')
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      setSocialText(data?.value || '');
    } catch (error) {
      console.error('Error fetching social text:', error);
    }
  };

  const fetchSocialLinks = async () => {
    try {
      const { data, error } = await supabase
        .from('social_links')
        .select('*')
        .order('platform', { ascending: true });

      if (error) throw error;
      setSocialLinks(data || []);
    } catch (error) {
      console.error('Error fetching social links:', error);
    }
  };

  return (
    <footer className="bg-[#E37C64] text-white py-6">
      <div className="container">
        <div className="flex flex-col items-center gap-4 md:gap-2 relative">
          <div className="text-center">
            <Logo className="w-64 brightness-0 invert" />
          </div>
          
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-sm text-center sm:text-left px-4">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <a href="tel:+31634413081" className="hover:underline">Renee: 06-34413081</a>
            </div>
            <span className="hidden sm:inline">|</span>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <a href="tel:+31620491340" className="hover:underline">Kitty: 06-20491340</a>
            </div>
            <span className="hidden sm:inline">|</span>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <a href="mailto:info@sisterhoodmemberclub.nl" className="hover:underline">info@sisterhoodmemberclub.nl</a>
            </div>
          </div>
          
          <div className="text-center w-full relative pt-6 sm:pt-4">
            <p className="text-base sm:text-lg mb-4 font-bold px-4 sm:px-0 text-center">
              {socialText}
            </p>
            <div className="flex gap-4 justify-center"> 
              {socialLinks.map(link => link.visible && (
                <a 
                  key={link.platform}
                  href={link.url} 
                  className="bg-white p-2 rounded-full hover:opacity-80 transition-opacity"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.platform === 'instagram' && (
                    <>
                      <Instagram className="w-6 h-6 text-[#E37C64]" />
                      <span className="sr-only">Instagram</span>
                    </>
                  )}
                  {link.platform === 'facebook' && (
                    <>
                      <Facebook className="w-6 h-6 text-[#E37C64]" />
                      <span className="sr-only">Facebook</span>
                    </>
                  )}
                </a>
              ))}
            </div>
          </div>
          <div className="text-center mt-4">
            <a 
              href="/assets/privacyverklaring.pdf" 
              className="text-xs hover:text-white/80 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacyverklaring
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;